import { useState, useEffect } from 'react';
import { Info, ShieldCheck, Eye } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, SaveButton, ConfigTimestamps, LoadingState } from './shared';
import { fetchMerchantConfig, createMerchantConfig } from '../../api/merchantConfigClient';

const SAMPLE_IMAGE = '/images/samples/whatsapp-order-confirmation.png';

const CRITERIA_OPTIONS = [
  { value: 'FORWARD&&COD', label: 'Forward + COD', description: 'Confirm orders that are Forward shipments with Cash on Delivery' },
  { value: 'FORWARD&&PREPAID', label: 'Forward + Prepaid', description: 'Confirm orders that are Forward shipments with Prepaid payment' },
];

function parseCriteriaToSelection(criteria) {
  const selected = new Set();
  if (!criteria || typeof criteria !== 'object') return selected;
  const raw = criteria.SHIPMENT_TYPE_AND_ORDER_TYPE;
  if (!raw) return selected;
  for (const part of String(raw).split('||')) {
    const trimmed = part.trim();
    if (trimmed) selected.add(trimmed);
  }
  return selected;
}

function selectionToCriteriaMap(selectedSet) {
  if (selectedSet.size === 0) return {};
  const joined = Array.from(selectedSet).join('||');
  return { SHIPMENT_TYPE_AND_ORDER_TYPE: joined };
}

export function OrderConfirmationTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS', reloadKey,
  );

  const [selectedCriteria, setSelectedCriteria] = useState(new Set());
  const [delayMinutes, setDelayMinutes] = useState('');
  const [waChargeAmount, setWaChargeAmount] = useState('');
  const [waChargeCurrency, setWaChargeCurrency] = useState('');
  const [ivrChargeAmount, setIvrChargeAmount] = useState('');
  const [ivrChargeCurrency, setIvrChargeCurrency] = useState('');
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState('');
  const [retryInterval, setRetryInterval] = useState('');

  useEffect(() => {
    if (!configValue) {
      setSelectedCriteria(new Set());
      setDelayMinutes('');
      setWaChargeAmount('');
      setWaChargeCurrency('INR');
      setIvrChargeAmount('');
      setIvrChargeCurrency('INR');
      setFallbackEnabled(false);
      setMaxAttempts('');
      setRetryInterval('');
      return;
    }

    setSelectedCriteria(parseCriteriaToSelection(configValue.create_order_confirmation_criteria));

    setDelayMinutes(configValue.confirmation_message_delay_in_minutes != null
      ? String(configValue.confirmation_message_delay_in_minutes) : '');

    const waCharge = configValue.whatsapp_confirmation_charge;
    if (waCharge && typeof waCharge === 'object') {
      setWaChargeAmount(waCharge.amount != null ? String(waCharge.amount) : '');
      setWaChargeCurrency(waCharge.currency ?? 'INR');
    } else {
      setWaChargeAmount('');
      setWaChargeCurrency('INR');
    }

    const ivrCharge = configValue.ivr_confirmation_charge;
    if (ivrCharge && typeof ivrCharge === 'object') {
      setIvrChargeAmount(ivrCharge.amount != null ? String(ivrCharge.amount) : '');
      setIvrChargeCurrency(ivrCharge.currency ?? 'INR');
    } else {
      setIvrChargeAmount('');
      setIvrChargeCurrency('INR');
    }

    const fallback = configValue.fallback_ivr_calling_configurations;
    if (fallback && typeof fallback === 'object') {
      setFallbackEnabled(!!fallback.is_fallback_ivr_calling_enabled);
      setMaxAttempts(fallback.max_fallback_ivr_calling_attempts != null
        ? String(fallback.max_fallback_ivr_calling_attempts) : '');
      setRetryInterval(fallback.fallback_ivr_calling_retry_interval_minutes != null
        ? String(fallback.fallback_ivr_calling_retry_interval_minutes) : '');
    } else {
      setFallbackEnabled(false);
      setMaxAttempts('');
      setRetryInterval('');
    }
  }, [configValue]);

  const toggleCriteria = (value) => {
    setSelectedCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedCriteria.size === 0) {
      alert('Please select at least one confirmation criteria (COD or Prepaid)');
      return;
    }

    const payload = {
      create_order_confirmation_criteria: selectionToCriteriaMap(selectedCriteria),
      confirmation_message_delay_in_minutes: delayMinutes !== '' ? parseInt(delayMinutes, 10) : undefined,
    };

    if (waChargeAmount !== '') {
      payload.whatsapp_confirmation_charge = {
        amount: parseFloat(waChargeAmount),
        currency: waChargeCurrency || 'INR',
      };
    }

    if (ivrChargeAmount !== '') {
      payload.ivr_confirmation_charge = {
        amount: parseFloat(ivrChargeAmount),
        currency: ivrChargeCurrency || 'INR',
      };
    }

    payload.fallback_ivr_calling_configurations = {
      is_fallback_ivr_calling_enabled: fallbackEnabled,
      max_fallback_ivr_calling_attempts: maxAttempts !== '' ? parseInt(maxAttempts, 10) : undefined,
      fallback_ivr_calling_retry_interval_minutes: retryInterval !== '' ? parseInt(retryInterval, 10) : undefined,
    };

    saveConfig({ merchant_create_order_confirmation_configurations_value: payload });

    try {
      let generalValue = {};
      try {
        const generalRaw = await fetchMerchantConfig({
          merchantId,
          configKey: 'MERCHANT_GENERAL_CONFIGURATIONS',
        });
        generalValue = generalRaw.merchant_general_configurations_value || {};
      } catch (e) {
        if (!e?.isNotFound) throw e;
      }
      generalValue.is_create_order_confirmation_check_enabled = true;
      generalValue.is_create_order_fulfillment_confirmation_request_for_channel_order_enabled = true;
      await createMerchantConfig({
        merchantId,
        configKey: 'MERCHANT_GENERAL_CONFIGURATIONS',
        body: { merchant_general_configurations_value: generalValue },
      });
    } catch { /* best-effort */ }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="flex flex-col xl:flex-row gap-4 xl:gap-5">
      <div className="flex-1 min-w-0 space-y-4">
        <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm text-blue-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">About Order Confirmation</p>
            <p className="text-[11px] text-blue-600 mt-0.5">
              Enable to confirm orders via WhatsApp before processing. Reduces fake orders and RTO rates.
            </p>
          </div>
        </div>

        {isNew && (
          <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs sm:text-sm text-amber-800">
            <span className="font-medium">No existing configuration</span> - select criteria below and save to create.
          </div>
        )}

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            Confirmation Criteria
          </h3>
          <p className="text-[11px] text-slate-500 mb-2">Select which order types require confirmation.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CRITERIA_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCriteria.has(opt.value)
                    ? 'border-blue-200 bg-blue-50/50 ring-1 ring-blue-500/20'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedCriteria.has(opt.value)}
                  onChange={() => toggleCriteria(opt.value)}
                  className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                  <p className="text-xs text-slate-500 mt-0.5">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Timing & Retry</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={FORM_STYLES.label}>Delay (minutes)</label>
              <input
                type="number"
                min="0"
                value={delayMinutes}
                onChange={(e) => setDelayMinutes(e.target.value)}
                placeholder="0"
                className={FORM_STYLES.input}
              />
              <p className="text-xs text-slate-400 mt-1">Wait time before sending confirmation.</p>
            </div>
            <div>
              <label className={FORM_STYLES.label}>Max Attempts</label>
              <input
                type="number"
                min="1"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                placeholder="3"
                className={FORM_STYLES.input}
              />
            </div>
            <div>
              <label className={FORM_STYLES.label}>Retry Interval (min)</label>
              <input
                type="number"
                min="1"
                value={retryInterval}
                onChange={(e) => setRetryInterval(e.target.value)}
                placeholder="30"
                className={FORM_STYLES.input}
              />
            </div>
          </div>
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>IVR Fallback</h3>
          <Toggle
            id="ivr-fallback"
            checked={fallbackEnabled}
            onChange={setFallbackEnabled}
            label="IVR Fallback Enabled"
            description="If WhatsApp confirmation fails, fall back to IVR call"
          />
        </div>

        <div className={FORM_STYLES.sectionCard}>
          <h3 className={FORM_STYLES.sectionTitle}>Charge Configuration</h3>
          <p className="text-[11px] text-slate-400 mb-3">*Inclusive of GST. Contact support to help.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">WhatsApp Confirmation</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FORM_STYLES.label}>Amount (₹)</label>
                  <input type="text" value={waChargeAmount} readOnly disabled className={FORM_STYLES.inputDisabled} />
                </div>
                <div>
                  <label className={FORM_STYLES.label}>Currency</label>
                  <input type="text" value={waChargeCurrency} readOnly disabled className={FORM_STYLES.inputDisabled} />
                </div>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">IVR Confirmation</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={FORM_STYLES.label}>Amount (₹)</label>
                  <input type="text" value={ivrChargeAmount} readOnly disabled className={FORM_STYLES.inputDisabled} />
                </div>
                <div>
                  <label className={FORM_STYLES.label}>Currency</label>
                  <input type="text" value={ivrChargeCurrency} readOnly disabled className={FORM_STYLES.inputDisabled} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <SaveButton saving={saving} onClick={handleSave} />
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>

      <div className="xl:w-[260px] 2xl:w-[300px] shrink-0 order-first xl:order-last">
        <div className="xl:sticky xl:top-4 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-500" />
              <span className="text-[10px] sm:text-xs font-semibold text-slate-600 uppercase tracking-wider">Sample Preview</span>
            </div>
            <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">How confirmation appears to customers</p>
          </div>
          <div className="p-2 sm:p-3">
            <img
              src={SAMPLE_IMAGE}
              alt="WhatsApp order confirmation sample"
              className="w-full h-auto rounded-lg border border-slate-100 shadow-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
